import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowSquareOut, CalendarBlank, CalendarCheck, ChatCircleText, Check, Coins, DeviceMobile, EnvelopeSimple, FileText, FolderSimple, Footprints, Gear,
  IdentificationCard, MagnifyingGlass, Note, Phone, Plus, SealCheck, SealWarning, ShieldCheck, Signature, Sparkle, Trash, UploadSimple, UsersThree, Wrench, X,
  type Icon,
} from '@phosphor-icons/react';
import {
  FeldRaster, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, PanelZeile, SchalterZeile, SegmentedControl, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { useIslandActions } from '../../../../../components/island/IslandProvider';
import { eur } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { objektName } from '../../../../../lib/immobilien/objektKopf';
import { normalisiereSuche } from '../../../../../lib/immobilien/suche';
import { terminTypLabel } from '../../../../../lib/termine/typLabel';
import { logAktivitaet, loescheNotiz, speichereNotiz } from '../../../../../lib/kontakte/api';
import { oeffneMieterDokument, ladeMieterDokumentHoch } from '../../../../../lib/mieter/mieterDokumente';
import { erfasseNachweis, setzeKuendigung } from '../../../../../lib/dienstleister/aktionen';
import { bauabzugNoetig, komma1 } from '../../../../../lib/dienstleister/detail';
import { istVertrag, zuordnungLaeuft } from '../../../../../lib/dienstleister/liste';
import { aktuelleNachweise, NACHWEIS_LABEL, type NachweisArt } from '../../../../../lib/dienstleister/nachweise';
import { DL_STATUS, statusText } from '../../../../../lib/dienstleister/status';
import { anforderungsMail, istUmlagefaehigeArt, naechsterKuendigungstermin, plusEinJahr } from '../../../../../lib/dienstleister/vertraege';
import { meldeDlGeaendert } from '../../../../../lib/dienstleister/useDienstleisterDetail';
import { AktivitaetBereich } from '../../../objekte/gemeinsam/WeitereBereiche';
import { TerminFormularKarte } from '../../../objekte/gemeinsam/TerminFormularKarte';
import { DatenZeile } from '../../../objekte/einheit/bereiche/teile';
import { useDlSeite } from '../DienstleisterSeitenKontext';
import { Aktionslink, BEREICH_STIL, fehlerText, GLAS } from './teile';

const ZEIT = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });

// ── Verträge (Screen 23) ───────────────────────────────────────────────────────
export function VertraegePanel() {
  const s = useDlSeite();
  const navigate = useNavigate();
  const d = s.daten.dienstleister;
  const [toast, zeige] = useToastPill();
  const [laeuft, setLaeuft] = useState(false);
  const obj = useMemo(() => new Map(s.daten.objekte.map(o => [o.id, o])), [s.daten.objekte]);
  const vertraege = s.daten.zuordnungen.filter(o => zuordnungLaeuft(o, s.heute) && istVertrag(o.vertragsart))
    .sort((a, b) => (a.kuendigung_zum ?? a.bis ?? '9999').localeCompare(b.kuendigung_zum ?? b.bis ?? '9999'));
  const umschalten = async (objektId: string, name: string, art: string, gekuendigt: boolean, bis: string | null) => {
    setLaeuft(true);
    try {
      const zum = gekuendigt ? null : naechsterKuendigungstermin(s.heute, bis);
      await setzeKuendigung(d.id, objektId, zum);
      zeige(zum ? `${art} ${name} gekündigt zum ${deDatum(zum)}` : 'Kündigung zurückgenommen');
      meldeDlGeaendert(d.id);
    } catch (err) { zeige(`Nicht geändert: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe>
        <KpiTile layout="kachel" label="Laufende Verträge" wert={s.vertrag.anzahl} icon={Signature} ton="acc" />
        <KpiTile layout="kachel" label="Kosten / Jahr" wert={s.vertrag.kostenJahr ? eur(s.vertrag.kostenJahr, { nachkomma: 0 }) : '–'} sub={s.vertrag.kostenJahr ? undefined : 'nicht hinterlegt'} icon={Coins} ton="info" />
        <KpiTile layout="kachel" label="Nächstes Vertragsende" wert={s.vertrag.naechstesEnde ? deDatum(s.vertrag.naechstesEnde) : '–'} icon={CalendarBlank} ton="warn" />
      </KpiReihe>
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>Verträge</KartenTitel></div>
        {vertraege.length === 0 && <span style={{ padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Nur Einzelaufträge – kein laufender Vertrag</span>}
        {vertraege.map(v => {
          const o = obj.get(v.objekt_id);
          const name = o ? objektName(o) : 'Objekt';
          const art = (v.vertragsart ?? '').trim();
          const gekuendigt = !!v.kuendigung_zum;
          const kosten = v.kosten_jahr === null || v.kosten_jahr === undefined ? null : Number(v.kosten_jahr);
          const sub = [
            v.von ? `seit ${deDatum(v.von)}` : '',
            gekuendigt ? `gekündigt zum ${deDatum(v.kuendigung_zum)}` : v.bis ? `endet ${deDatum(v.bis)}` : 'unbefristet, 3 Mon. zum Jahresende kündbar',
            kosten ? `${eur(kosten, { nachkomma: 0 })} / Jahr` : '', v.umlagefaehig ? 'umlagefähig' : (istUmlagefaehigeArt(art) ? 'Umlagefähigkeit prüfen' : ''),
          ].filter(Boolean).join(' · ');
          return (
            <PanelZeile key={v.objekt_id} icon={Signature} ton={gekuendigt ? 'warn' : 'acc'} titel={`${art} · ${name}`} sub={sub}
              chip={gekuendigt ? { text: 'gekündigt', ton: 'warn' } : { text: 'aktiv', ton: 'ok' }}
              onClick={laeuft ? undefined : () => { void umschalten(v.objekt_id, name, art, gekuendigt, v.bis); }}
              ariaLabel={gekuendigt ? `Kündigung ${art} ${name} zurücknehmen` : `${art} ${name} kündigen`} />
          );
        })}
      </InnenKarte>
      <InnenKarte style={GLAS} gap={4}>
        <KartenTitel>Hinweis</KartenTitel>
        <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>
          Antippen kündigt einen Vertrag zum nächstmöglichen Termin bzw. nimmt die Kündigung zurück. Umlagefähige Verträge erscheinen in der Betriebskostenabrechnung der Objekte.
        </span>
      </InnenKarte>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton groesse={44} icon={Plus} onClick={() => s.oeffnePanel('objekte')}>Vertrag hinzufügen</ImmoButton>
        <ImmoButton groesse={44} icon={ArrowSquareOut} onClick={() => navigate('/admin/vertraege')}>Zum Vertragsbereich</ImmoButton>
      </div>
    </>
  );
}

// ── Nachweise & Versicherungen (Screens 24, 34) ────────────────────────────────
const NACHWEIS_ICON: Record<NachweisArt, Icon> = { freistellung_48b: SealCheck, haftpflicht: ShieldCheck, gewerbe: IdentificationCard, mindestlohn: FileText };
const NACHWEIS_TON = { gueltig: 'ok', laeuft_ab: 'warn', abgelaufen: 'bad' } as const;
const NACHWEIS_TEXT = { gueltig: 'gültig', laeuft_ab: 'läuft ab', abgelaufen: 'abgelaufen' } as const;

export function NachweisePanel() {
  const s = useDlSeite();
  const d = s.daten.dienstleister;
  const z = s.daten.zeile;
  const [toast, zeige] = useToastPill();
  const [laeuft, setLaeuft] = useState(false);
  const [form, setForm] = useState<{ art: NachweisArt; bis: string; unbefristet: boolean } | null>(null);
  const [datei, setDatei] = useState<File | null>(null);
  const eingabe = useRef<HTMLInputElement>(null);
  const vorhanden = s.nachweise.filter(n => n.vorhanden);
  const zahl = (st: 'gueltig' | 'laeuft_ab' | 'abgelaufen') => vorhanden.filter(n => n.status === st && n.gueltigBis).length;
  const dokVon = new Map(aktuelleNachweise(s.daten.nachweise.map(n => ({ ...n, dienstleister_id: d.id }))).map(n => [n.art, n.dokument_id]));
  const mail = anforderungsMail(s.name, s.email, s.nachweise);
  const speichere = async () => {
    if (!form) return;
    setLaeuft(true);
    try {
      const warn = await erfasseNachweis(d.id, s.name, form.art, form.unbefristet ? null : form.bis, datei);
      zeige([`${NACHWEIS_LABEL[form.art]} ${form.unbefristet ? 'unbefristet' : `gültig bis ${deDatum(form.bis)}`}`, ...warn].join(' · '), warn.length > 0);
      setForm(null); setDatei(null);
      meldeDlGeaendert(d.id);
    } catch (err) { zeige(`Nicht gespeichert: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe>
        <KpiTile layout="kachel" label="Gültig" wert={zahl('gueltig')} icon={SealCheck} ton="ok" />
        <KpiTile layout="kachel" label="Läuft ab (60 Tage)" wert={zahl('laeuft_ab')} icon={SealWarning} ton="warn" />
        <KpiTile layout="kachel" label="Abgelaufen" wert={zahl('abgelaufen')} icon={SealWarning} ton="bad" />
      </KpiReihe>
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>Nachweise</KartenTitel></div>
        {s.nachweise.map(n => {
          const dokId = dokVon.get(n.art) ?? null;
          const dok = dokId ? s.daten.dokumente.find(x => x.id === dokId) ?? null : null;
          const sub = [n.vorhanden ? (n.gueltigBis ? `gültig bis ${deDatum(n.gueltigBis)}` : 'unbefristet') : 'nicht hinterlegt', dok ? 'Dokument hinterlegt' : '', 'antippen: neuen Nachweis erfassen (+1 Jahr)'].filter(Boolean).join(' · ');
          return (
            <div key={n.art} className="flex items-center min-w-0" style={{ gap: 4 }}>
              <div className="flex-1 min-w-0">
                <PanelZeile icon={NACHWEIS_ICON[n.art]} ton={n.vorhanden ? NACHWEIS_TON[n.status] : 'neutral'} titel={n.label} sub={sub}
                  chip={n.vorhanden ? { text: NACHWEIS_TEXT[n.status], ton: NACHWEIS_TON[n.status] } : { text: 'fehlt', ton: n.art === 'freistellung_48b' && z.handwerk ? 'bad' : 'neutral' }}
                  onClick={() => { setForm({ art: n.art, bis: plusEinJahr(s.heute), unbefristet: n.art === 'gewerbe' }); setDatei(null); }} ariaLabel={`${n.label} neu erfassen`} />
              </div>
              {dok && <ImmoButton groesse={32} icon={ArrowSquareOut} aria-label={`${n.label} öffnen`} title="Dokument öffnen"
                onClick={() => { void oeffneMieterDokument(dok).then(ok => { if (!ok) zeige('Datei nur im Dokumentenbereich erreichbar', true); }); }} />}
            </div>
          );
        })}
      </InnenKarte>
      {form && (
        <InnenKarte style={GLAS}>
          <KartenTitel>{`Nachweis erfassen: ${NACHWEIS_LABEL[form.art]}`}</KartenTitel>
          <FeldRaster minBreite={200}>
            <TextFeld label="gültig bis" icon={CalendarBlank} type="date" wert={form.unbefristet ? '' : form.bis} onChange={v => setForm(f => (f ? { ...f, bis: v } : f))} disabled={form.unbefristet} />
          </FeldRaster>
          <SchalterZeile an={form.unbefristet} onChange={an => setForm(f => (f ? { ...f, unbefristet: an } : f))} label="Unbefristet" klein />
          {datei && <span style={{ fontSize: 12, color: 'var(--ink2)' }}>Datei: {datei.name} – wird unter „Dokumente“ abgelegt.</span>}
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <ImmoButton art="akzent" icon={Check} disabled={laeuft || (!form.unbefristet && !form.bis)} onClick={() => { void speichere(); }}>{laeuft ? 'Wird gespeichert …' : 'Nachweis speichern'}</ImmoButton>
            <ImmoButton icon={UploadSimple} disabled={laeuft} onClick={() => eingabe.current?.click()}>{datei ? 'Andere Datei wählen' : 'Nachweis hochladen'}</ImmoButton>
            <ImmoButton icon={X} disabled={laeuft} onClick={() => { setForm(null); setDatei(null); }}>Abbrechen</ImmoButton>
            <input ref={eingabe} type="file" hidden accept=".pdf,image/*" onChange={ev => { setDatei(ev.target.files?.[0] ?? null); ev.target.value = ''; }} />
          </div>
        </InnenKarte>
      )}
      {z.handwerk && (
        <InnenKarte style={bauabzugNoetig(z.handwerk, s.nachweise) ? { background: 'var(--bad-soft)' } : GLAS} gap={4}>
          <KartenTitel>§ 48b EStG</KartenTitel>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>
            Ohne gültige Freistellungsbescheinigung müssen bei Bauleistungen 15 % Bauabzugsteuer einbehalten werden. Die Bescheinigung kann beim Bundeszentralamt für Steuern online geprüft werden.
          </span>
        </InnenKarte>
      )}
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        {mail ? <Aktionslink href={mail} icon={EnvelopeSimple}>Nachweise anfordern</Aktionslink>
          : <ImmoButton groesse={44} icon={EnvelopeSimple} disabled title="Keine E-Mail hinterlegt">Nachweise anfordern</ImmoButton>}
        <Aktionslink href="https://www.bzst.de" icon={ArrowSquareOut} extern>BZSt-Abfrage</Aktionslink>
        <ImmoButton groesse={44} icon={UploadSimple} onClick={() => { setForm({ art: z.handwerk ? 'freistellung_48b' : 'haftpflicht', bis: plusEinJahr(s.heute), unbefristet: false }); setDatei(null); }}>Nachweis hochladen</ImmoButton>
      </div>
    </>
  );
}

// ── Dienstleisterportal (Screen 28) – noch nicht angebunden (Entscheidung 14.10.2026) ──
const RECHTE = ['Aufträge annehmen & Status melden', 'Termine vorschlagen', 'Fotos & Arbeitsberichte hochladen', 'Rechnungen hochladen', 'Objektinfos & Zugang (Schlüsselsafe) sehen', 'Mieterkontakt direkt sehen'];

export function PortalPanel() {
  const s = useDlSeite();
  return (
    <>
      <KpiReihe>
        <KpiTile layout="kachel" label="Status" wert="Kein Portal" icon={DeviceMobile} ton="neutral" />
        <KpiTile layout="kachel" label="Login / Einladung an" wert={s.email || '–'} sub={s.email ? undefined : 'keine E-Mail hinterlegt'} icon={EnvelopeSimple} ton="info" />
      </KpiReihe>
      <InnenKarte style={GLAS}>
        <KartenTitel>Zugang freigeben</KartenTitel>
        <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>
          Das Dienstleisterportal ist noch nicht angebunden. Einladung, Passwort-Reset, Sperren und die Berechtigungen werden freigeschaltet, sobald es eine Portal-App für Dienstleister gibt.
        </span>
        <div className="flex flex-col" style={{ gap: 6 }}>
          {RECHTE.map(r => <SchalterZeile key={r} an={false} onChange={() => undefined} disabled label={r} klein zusatz="noch nicht angebunden" />)}
        </div>
        <ImmoButton art="akzent" icon={DeviceMobile} disabled title="Dienstleisterportal noch nicht angebunden" className="self-start">Zugang freigeben &amp; Einladung senden</ImmoButton>
      </InnenKarte>
    </>
  );
}

// ── Termine (Screen 29) ────────────────────────────────────────────────────────
type TerminArt = 'einsatz' | 'wartung' | 'begehung' | 'gespraech';
const TERMIN_ARTEN: readonly { key: TerminArt; label: string; icon: Icon; typ: string }[] = [
  { key: 'einsatz', label: 'Einsatz', icon: Wrench, typ: 'sonstig' }, { key: 'wartung', label: 'Wartung', icon: Gear, typ: 'wartung' },
  { key: 'begehung', label: 'Begehung', icon: Footprints, typ: 'besichtigung' }, { key: 'gespraech', label: 'Gespräch', icon: UsersThree, typ: 'meeting' },
];

export function TerminePanel() {
  const s = useDlSeite();
  const d = s.daten.dienstleister;
  const navigate = useNavigate();
  const { onSearchNavigate } = useIslandActions();
  const [art, setArt] = useState<TerminArt | null>(null);
  const [toast, zeige] = useToastPill();
  const objName = new Map(s.daten.objekte.map(o => [o.id, objektName(o)]));
  const termine = [...s.daten.termine].sort((a, b) => a.start_datum.localeCompare(b.start_datum));
  const kuenftig = termine.filter(t => t.start_datum.slice(0, 10) >= s.heute);
  const vergangen = termine.filter(t => t.start_datum.slice(0, 10) < s.heute).reverse().slice(0, 10);
  const oeffne = (id: string) => { if (onSearchNavigate) onSearchNavigate('/admin/kalender', id, 'termin'); else navigate('/admin/kalender'); };
  const gewaehlt = art ? TERMIN_ARTEN.find(a => a.key === art)! : null;
  const zeile = (t: (typeof termine)[number], alt: boolean) => {
    const dd = new Date(t.start_datum);
    const zeit = Number.isNaN(dd.getTime()) || t.start_datum.length <= 10 ? 'ganztägig' : `${ZEIT.format(dd)} Uhr`;
    return (
      <PanelZeile key={t.id} icon={CalendarCheck} ton={alt ? 'neutral' : 'acc'} aktiv={!alt} titel={t.titel || 'Termin'}
        sub={[t.typ ? terminTypLabel({ typ: t.typ }) : '', zeit, t.objekt_id ? objName.get(t.objekt_id) ?? '' : ''].filter(Boolean).join(' · ')}
        rechts={deDatum(t.start_datum.slice(0, 10))} chip={t.start_datum.slice(0, 10) === s.heute ? { text: 'heute', ton: 'ok' } : undefined}
        onClick={() => oeffne(t.id)} ariaLabel={`${t.titel || 'Termin'} im Kalender öffnen`} />
    );
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Termin anlegen</KartenTitel>
        <SegmentedControl<TerminArt | ''> ariaLabel="Art" hoehe={34} className="self-start" wert={art ?? ''} onChange={v => setArt(v || null)}
          segmente={TERMIN_ARTEN.map(a => ({ key: a.key, label: a.label, icon: a.icon }))} />
        {!gewaehlt && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Art wählen – der Termin wird mit {s.name} verknüpft. Mieter informieren Sie über die Teilnehmer bzw. die Aushang-Funktion am Objekt.</span>}
      </InnenKarte>
      {gewaehlt && (
        <TerminFormularKarte key={gewaehlt.key} termin={null} prefillDienstleisterId={d.id} prefillObjektId={s.daten.zeile.objektIds[0] ?? null} prefillTyp={gewaehlt.typ}
          prefillTitel={`${gewaehlt.label} ${s.name}`} onAbbrechen={() => setArt(null)}
          onGespeichert={() => { setArt(null); zeige('Termin angelegt'); meldeDlGeaendert(d.id); }} />
      )}
      <InnenKarte style={GLAS}>
        <KartenTitel>{`${kuenftig.length} ${kuenftig.length === 1 ? 'Termin' : 'Termine'}`}</KartenTitel>
        {kuenftig.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine anstehenden Termine.</span>}
        <div className="flex flex-col">{kuenftig.map(t => zeile(t, false))}</div>
        {vergangen.length > 0 && (
          <>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)', marginTop: 8 }}>Vergangen</span>
            <div className="flex flex-col">{vergangen.map(t => zeile(t, true))}</div>
          </>
        )}
      </InnenKarte>
    </>
  );
}

// ── Kommunikation (Screen 30) ──────────────────────────────────────────────────
type LogTyp = 'anruf' | 'mail' | 'meeting' | 'notiz';
const LOG_TYPEN: readonly { key: LogTyp; label: string; icon: Icon }[] = [
  { key: 'anruf', label: 'Anruf', icon: Phone }, { key: 'mail', label: 'Mail', icon: EnvelopeSimple },
  { key: 'meeting', label: 'Meeting', icon: UsersThree }, { key: 'notiz', label: 'Notiz', icon: Note },
];

export function KommunikationPanel() {
  const s = useDlSeite();
  const d = s.daten.dienstleister;
  const [typ, setTyp] = useState<LogTyp>('anruf');
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [toast, zeige] = useToastPill();
  const eintragen = async () => {
    const b = text.trim();
    if (!b) return;
    setLaeuft(true);
    try {
      if (typ === 'notiz') await speichereNotiz('dienstleister', d.id, b);
      else await logAktivitaet('dienstleister', d.id, { typ, betreff: b, richtung: typ === 'meeting' ? null : 'ausgehend' });
      setText('');
      zeige(`${LOG_TYPEN.find(t => t.key === typ)?.label ?? 'Eintrag'} protokolliert`);
      meldeDlGeaendert(d.id);
    } catch (err) { zeige(`Nicht eingetragen: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Schnell loggen</KartenTitel>
        <SegmentedControl ariaLabel="Art" hoehe={34} className="self-start" wert={typ} onChange={setTyp} segmente={LOG_TYPEN.map(t => ({ key: t.key, label: t.label, icon: t.icon }))} />
        <form className="flex flex-col" style={{ gap: 12 }} onSubmit={ev => { ev.preventDefault(); void eintragen(); }}>
          <TextFeld label="Worum ging es?" icon={ChatCircleText} wert={text} onChange={setText} placeholder="Kurzbeschreibung" maxLength={300} />
          <div className="flex justify-end">
            <ImmoButton type="submit" art="akzent" groesse={44} icon={Plus} disabled={laeuft || !text.trim()}>{laeuft ? 'Wird eingetragen …' : 'Eintragen'}</ImmoButton>
          </div>
        </form>
      </InnenKarte>
      <KartenTitel>Verlauf</KartenTitel>
      <AktivitaetBereich aktivitaeten={s.daten.aktivitaeten} hinweis={null} />
    </>
  );
}

// ── Dokumente (Screen 31) ──────────────────────────────────────────────────────
export function DokumentePanel() {
  const s = useDlSeite();
  const d = s.daten.dienstleister;
  const [suche, setSuche] = useState('');
  const [kategorie, setKategorie] = useState<string>('alle');
  const [laedtHoch, setLaedtHoch] = useState(false);
  const datei = useRef<HTMLInputElement>(null);
  const [toast, zeige] = useToastPill();
  const docs = s.daten.dokumente;
  const kategorien = useMemo(() => [...new Set(docs.map(x => (x.kategorie ?? '').trim() || 'Ohne Kategorie'))].sort((a, b) => a.localeCompare(b, 'de')), [docs]);
  const sichtbar = useMemo(() => {
    const q = normalisiereSuche(suche);
    return docs.filter(x => (kategorie === 'alle' || ((x.kategorie ?? '').trim() || 'Ohne Kategorie') === kategorie)
      && (!q || normalisiereSuche(`${x.titel ?? ''} ${x.datei_name ?? ''} ${x.kategorie ?? ''}`).includes(q)));
  }, [docs, suche, kategorie]);
  const hochladen = async (f: File) => {
    setLaedtHoch(true);
    try {
      await ladeMieterDokumentHoch({ datei: f, mieterId: d.id, mieterName: s.name, objektId: null, kategorie: 'Dienstleister', ordner: 'dienstleister', dienstleister: true });
      zeige(`${f.name} hochgeladen`);
      meldeDlGeaendert(d.id);
    } catch (err) { zeige(`Upload fehlgeschlagen: ${fehlerText(err)}`, true); } finally {
      setLaedtHoch(false);
      if (datei.current) datei.current.value = '';
    }
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Suchen</KartenTitel>
        <TextFeld label="Dokument" icon={MagnifyingGlass} wert={suche} onChange={setSuche} placeholder="Dokumente durchsuchen" />
        {kategorien.length > 1 && (
          <SegmentedControl ariaLabel="Kategorie" hoehe={30} className="self-start" wert={kategorie} onChange={setKategorie}
            segmente={[{ key: 'alle', label: 'Alle' }, ...kategorien.map(k => ({ key: k, label: k }))]} />
        )}
        <div className="flex justify-end">
          <input ref={datei} type="file" hidden onChange={e => { const f = e.target.files?.[0]; if (f) void hochladen(f); }} />
          <ImmoButton art="akzent" groesse={44} icon={UploadSimple} disabled={laedtHoch} onClick={() => datei.current?.click()}>{laedtHoch ? 'Wird hochgeladen …' : 'Hochladen'}</ImmoButton>
        </div>
      </InnenKarte>
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>{`${sichtbar.length} ${sichtbar.length === 1 ? 'Dokument' : 'Dokumente'}`}</KartenTitel></div>
        {sichtbar.length === 0 && <span style={{ padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>{docs.length ? 'Keine Dokumente gefunden' : 'Noch keine Dokumente zu diesem Dienstleister.'}</span>}
        {sichtbar.map(x => (
          <PanelZeile key={x.id} icon={FolderSimple} ton="acc" titel={x.titel || x.datei_name || 'Dokument'}
            sub={[(x.kategorie ?? '').trim() || 'Ohne Kategorie', deDatum(x.created_at.slice(0, 10))].join(' · ')} rechts={<ArrowSquareOut size={15} color="var(--ink3)" />}
            onClick={() => { void oeffneMieterDokument(x).then(ok => { if (!ok) zeige('Datei nur im Dokumentenbereich erreichbar', true); }); }} ariaLabel={`${x.titel || x.datei_name || 'Dokument'} öffnen`} />
        ))}
      </InnenKarte>
    </>
  );
}

// ── Notizen (Screen 32) ────────────────────────────────────────────────────────
export function NotizenPanel() {
  const s = useDlSeite();
  const d = s.daten.dienstleister;
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [toast, zeige] = useToastPill();
  const speichere = async () => {
    const t = text.trim();
    if (!t) return;
    setLaeuft(true);
    try { await speichereNotiz('dienstleister', d.id, t); setText(''); zeige('Notiz gespeichert'); meldeDlGeaendert(d.id); }
    catch (err) { zeige(`Nicht gespeichert: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };
  const loesche = async (id: string) => {
    try { await loescheNotiz(id); zeige('Notiz gelöscht'); meldeDlGeaendert(d.id); } catch (err) { zeige(`Nicht gelöscht: ${fehlerText(err)}`, true); }
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Neue Notiz</KartenTitel>
        <textarea value={text} onChange={e => setText(e.target.value)} placeholder={`Notiz zu ${s.name} …`} rows={4} aria-label="Neue Notiz" style={BEREICH_STIL} />
        <div className="flex justify-end">
          <ImmoButton art="akzent" groesse={44} icon={Plus} disabled={laeuft || !text.trim()} onClick={() => { void speichere(); }}>{laeuft ? 'Wird gespeichert …' : 'Notiz speichern'}</ImmoButton>
        </div>
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel rechts={s.daten.notizen.length ? `${s.daten.notizen.length}` : undefined}>Notizen</KartenTitel>
        {s.daten.notizen.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Notizen</span>}
        <div className="flex flex-col">
          {s.daten.notizen.map(n => (
            <div key={n.id} className="flex items-start" style={{ gap: 12, padding: '10px 6px', borderBottom: '1px solid var(--line)' }}>
              <span className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
                <span style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{n.text}</span>
                <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>{deDatum(n.created_at.slice(0, 10))}</span>
              </span>
              <ImmoButton groesse={32} pille={false} art="bad" icon={Trash} aria-label="Notiz löschen" title="Notiz löschen" onClick={() => { void loesche(n.id); }} />
            </div>
          ))}
        </div>
      </InnenKarte>
    </>
  );
}

// ── KI-Zusammenfassung ─────────────────────────────────────────────────────────
export function KiPanel() {
  const s = useDlSeite();
  const z = s.daten.zeile;
  const st = DL_STATUS[z.status];
  const ab = s.nachweise.filter(n => n.vorhanden && n.status === 'abgelaufen').length;
  const bald = s.nachweise.filter(n => n.vorhanden && n.status === 'laeuft_ab').length;
  const farbe = (ton: string) => (ton === 'neutral' ? 'var(--ink)' : `var(--${ton})`);
  return (
    <>
      <InnenKarte style={GLAS}>
        <div className="flex items-start" style={{ gap: 14 }}>
          <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: 'var(--acc-soft)' }}><Sparkle weight="fill" size={20} color="var(--acc)" /></span>
          <div className="flex flex-col min-w-0" style={{ gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>Zusammenfassung</span>
            <span style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)' }}>{s.ki}</span>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Automatisch aus den Fakten erstellt – eine KI-Formulierung wird noch angebunden.</span>
          </div>
        </div>
      </InnenKarte>
      <InnenKarte style={GLAS} gap={0}>
        <KartenTitel>Fakten</KartenTitel>
        <DatenZeile label="Status" wert={<span style={{ color: farbe(st.ton) }}>{statusText(z.status, z.vertragEndet)}</span>} />
        <DatenZeile label="Gewerke" wert={z.kategorien.join(', ') || '–'} />
        <DatenZeile label="Bewertung" wert={z.bewertung === null ? '–' : `${komma1(z.bewertung)} von 5`} />
        <DatenZeile label="Offene Aufträge" wert={String(z.offeneAuftraege)} />
        <DatenZeile label="Offene Rechnungen" wert={<span style={{ color: z.offen > 0.005 ? 'var(--warn)' : 'var(--ok)' }}>{z.offen > 0.005 ? eur(z.offen) : 'keine'}</span>} />
        <DatenZeile label="Nachweise" wert={<span style={{ color: ab ? 'var(--bad)' : bald ? 'var(--warn)' : 'var(--ok)' }}>{ab ? `${ab} abgelaufen` : bald ? `${bald} läuft ab` : 'in Ordnung'}</span>} />
        <DatenZeile label="Objekte" wert={z.objektNamen.length ? `${z.objektNamen.length}` : 'keine'} />
        <DatenZeile label="Portal" wert={<span style={{ color: 'var(--ink3)' }}>Kein Portal</span>} />
      </InnenKarte>
    </>
  );
}

